import React, { useState } from "react";
import { Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SOCIAL = ["google", "facebook"];

// Google/Facebook accounts must add a mobile number once, so their orders, loyalty and CRM profile join up.
export default function PhoneRequiredModal() {
  const { user, adoptSession, logout } = useAuth();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user || user.role !== "customer" || user.phone || !SOCIAL.includes(user.provider)) return null;
  const digits = phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
  const valid = /^[6-9]\d{9}$/.test(digits);

  const save = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", { phone: digits });
      adoptSession({ ...user, phone: data.phone });
      toast.success("Phone saved — thanks!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save your phone");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-biga-ink/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="phone-required-modal">
      <form onSubmit={save} className="bg-biga-cream rounded-3xl p-6 w-full max-w-sm shadow-2xl">
        <div className="w-12 h-12 rounded-full bg-biga-orange/15 text-biga-orange flex items-center justify-center mb-3"><Phone size={22} /></div>
        <h2 className="font-display font-black text-2xl text-biga-ink">Add your mobile number</h2>
        <p className="text-sm text-biga-ink/60 mt-1 mb-4">Hi {user.name?.split(" ")[0] || "there"}! We need your number once to send order updates and link your pizza points to every order.</p>
        <div className="flex items-center gap-2 rounded-xl border-2 border-biga-ink/15 bg-white px-3 focus-within:border-biga-orange">
          <span className="text-sm font-bold text-biga-ink/50">+91</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel-national" placeholder="98765 43210" autoFocus
            className="flex-1 py-3 text-base outline-none focus:outline-none focus-visible:outline-none bg-transparent" data-testid="phone-required-input" />
        </div>
        {phone && !valid && <p className="text-xs text-red-600 mt-1" data-testid="phone-required-error">Enter a 10-digit Indian mobile number</p>}
        <button type="submit" disabled={!valid || busy} data-testid="phone-required-save"
          className="mt-4 w-full bg-biga-orange text-white font-bold py-3 rounded-full disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} Save and continue
        </button>
        <button type="button" onClick={logout} data-testid="phone-required-signout" className="mt-2 w-full text-xs font-bold text-biga-ink/45 py-2">Sign out instead</button>
      </form>
    </div>
  );
}
